import {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Hint} from '@/components/ui/hint'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {InvitarExternoDialog} from '@/components/invitar-externo-dialog'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  RESPONSABLE_STANDARD,
  COMMITTEE_STANDARDS,
  useCreateDesignation,
  type DesignationInput,
} from '@/hooks/sgsst/evaluacion/use-designations'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
  mintUploadTarget,
  uploadToStorage,
  useRegisterEvidence,
  type EvidenceList,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {translateApiError} from '@/lib/errors'

export type DesignationFormInitial = {
  party?: 'empleador' | 'trabajadores' | null
  rol?: 'principal' | 'suplente' | null
}

function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

const selectClass = 'h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground'

/** R3 (`SearchComboboxShell`). Each option shows the person's licencia SST and vínculo; never blocks
 * saving: a designation without a licensed person is legally valid, just uncertifiable. */
function PersonSelect({people, value, onChange}: {people: Employee[]; value: string; onChange: (id: string) => void}) {
  const {t} = useTranslation()
  const actives = people.filter((e) => e.status === 'active')
  const vinculoLabel = (e: Employee) =>
    t(e.vinculo === 'externo' ? 'designations.form.personExterno' : 'designations.form.personNomina')
  return (
    <EntitySearchSelect
      id="designation-person"
      items={actives}
      value={actives.find((e) => e.id === value) ?? null}
      getKey={(e) => e.id}
      filterText={(e) => `${e.name} ${e.last_name} ${e.cedula} ${vinculoLabel(e)}`}
      renderOption={(e) => (
        <span>
          {e.name} {e.last_name} · <span className="font-mono text-xs">{e.cedula}</span>
          <span className="text-muted-foreground"> · {vinculoLabel(e)}</span>
          {e.licencia_estado == null && (
            <span className="text-muted-foreground"> · {t('designations.form.personOptionNoLicencia')}</span>
          )}
        </span>
      )}
      renderTriggerValue={(e) => (
        <span>
          {e.name} {e.last_name}
          {e.vinculo === 'externo' && <span className="text-muted-foreground"> · {vinculoLabel(e)}</span>}
        </span>
      )}
      placeholder={t('designations.form.personPlaceholder')}
      searchPlaceholder={t('designations.form.personSearchPlaceholder')}
      noResultsLabel={t('designations.form.personNoResults')}
      testId="designation-person"
      onPick={(e) => onChange(e?.id ?? '')}
    />
  )
}

export function DesignationFormDialog({
  businessId,
  standardCode,
  standardName,
  roster,
  open,
  onOpenChange,
  initial,
}: {
  businessId: string
  standardCode: string
  standardName: string
  roster: Employee[]
  open: boolean
  onOpenChange: (open: boolean) => void
  initial?: DesignationFormInitial
}) {
  const {t} = useTranslation()
  const isCommittee = COMMITTEE_STANDARDS.has(standardCode)
  const isResponsable = standardCode === RESPONSABLE_STANDARD
  // Los externos con acceso solo se suman en el 1.1.1: comités, COPASST y brigadas se integran
  // con trabajadores.
  const {data: externos} = useAllEmployees(isResponsable && open ? businessId : undefined, 'externo')
  // Dar acceso a un externo es del dueño (`require_role(owner)`): al responsable se le esconde la
  // salida en vez de dejarlo chocar con un 403. Mismo gate que «Externos con acceso».
  const {data: business} = useBusiness(open ? businessId : undefined)
  const godReadOnly = useGodModeReadOnly()
  const canAlta = business?.role === 'owner' && !godReadOnly
  const registerEvidence = useRegisterEvidence(businessId)
  const createDesignation = useCreateDesignation(businessId)
  const fileRef = useRef<HTMLInputElement>(null)

  const [employeeId, setEmployeeId] = useState('')
  const [party, setParty] = useState('')
  const [rol, setRol] = useState('')
  const [effectiveDate, setEffectiveDate] = useState(todayIso())
  const [carta, setCarta] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [altaOpen, setAltaOpen] = useState(false)
  // Seleccionada ya: invalidar la consulta es asíncrono. Se deduplica cuando el refetch la trae.
  const [recienDadaDeAlta, setRecienDadaDeAlta] = useState<Employee | null>(null)
  const people = [...roster, ...(externos ?? [])]
  if (recienDadaDeAlta && !people.some((e) => e.id === recienDadaDeAlta.id)) people.push(recienDadaDeAlta)
  const selectedPersonNoLicencia = people.find((e) => e.id === employeeId)?.licencia_estado == null && !!employeeId

  // Controlled from outside: a programmatic open must reset here, Radix only reports user closes.
  useEffect(() => {
    if (!open) return
    setError(null)
    setEmployeeId('')
    setParty(initial?.party ?? '')
    setRol(initial?.rol ?? '')
    setEffectiveDate(todayIso())
    setCarta(null)
    setAltaOpen(false)
    setRecienDadaDeAlta(null)
  }, [open, initial])

  function handleOpenChange(next: boolean) {
    onOpenChange(next)
  }

  function pickCarta(files: FileList | null) {
    const file = files?.[0] ?? null
    if (!file) return
    if (!EVIDENCE_ALLOWED_TYPES.has(file.type)) {
      setError(t('errors.evidenceBadFormat'))
      return
    }
    if (file.size > EVIDENCE_MAX_BYTES) {
      setError(evidenceTooBig(t, file))
      return
    }
    setError(null)
    setCarta(file)
  }

  async function uploadCarta(file: File): Promise<string | null> {
    const target = await mintUploadTarget(businessId, file)
    await uploadToStorage(target, file, () => {})
    const response = await registerEvidence.mutateAsync({
      standard_code: standardCode,
      object_key: target.object_key,
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
      employee_ids: [],
    })
    // the list orders each standard by upload time: the last match is ours
    const list = response.data as EvidenceList
    const mine = list.items.filter((i) => i.standard_code === standardCode && i.filename === file.name).at(-1)
    return mine?.id ?? null
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      if (!employeeId) throw new Error(t('designations.form.personRequired'))
      const input: DesignationInput = {
        standard_code: standardCode,
        employee_id: employeeId,
        party: isCommittee ? (party as DesignationInput['party']) : null,
        rol: isCommittee ? (rol as DesignationInput['rol']) : null,
        effective_date: effectiveDate,
        carta_evidence_id: carta ? await uploadCarta(carta) : null,
      }
      await createDesignation.mutateAsync(input)
      handleOpenChange(false)
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : translateApiError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('designations.form.title')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t('designations.form.subtitle', {code: standardCode, name: standardName})}
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
          className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="designation-person">{t('designations.form.person')}</Label>
            <PersonSelect people={people} value={employeeId} onChange={setEmployeeId} />
            {isResponsable && canAlta && (
              <button
                type="button"
                data-testid="designation-alta-externo"
                className="text-xs font-medium text-primary hover:underline"
                onClick={() => setAltaOpen(true)}>
                {t('designations.form.altaExterno')}
              </button>
            )}
            {selectedPersonNoLicencia && (
              <p
                className="tone-amber rounded-lg px-2.5 py-1.5 text-xs leading-snug"
                data-testid="designation-person-no-licencia-warning">
                {t('designations.form.personNoLicenciaWarning')}
              </p>
            )}
          </div>

          {isCommittee && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="designation-party">{t('designations.form.party')}</Label>
                <select
                  id="designation-party"
                  data-testid="designation-party"
                  value={party}
                  onChange={(e) => setParty(e.target.value)}
                  className={selectClass}>
                  <option value="">{t('designations.form.partyPlaceholder')}</option>
                  <option value="empleador">{t('designations.party.empleador')}</option>
                  <option value="trabajadores">{t('designations.party.trabajadores')}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="designation-rol">{t('designations.form.rol')}</Label>
                <select
                  id="designation-rol"
                  data-testid="designation-rol"
                  value={rol}
                  onChange={(e) => setRol(e.target.value)}
                  className={selectClass}>
                  <option value="">{t('designations.form.rolPlaceholder')}</option>
                  <option value="principal">{t('designations.rol.principal')}</option>
                  <option value="suplente">{t('designations.rol.suplente')}</option>
                </select>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="designation-date">{t('designations.form.date')}</Label>
            <Input
              id="designation-date"
              type="date"
              className="max-w-45 font-mono"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>{t('designations.form.carta')}</Label>
            <input
              ref={fileRef}
              type="file"
              accept={EVIDENCE_ACCEPT}
              hidden
              data-testid="designation-carta-input"
              onChange={(e) => pickCarta(e.target.files)}
            />
            <div className="flex items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
                {t('designations.form.attach')}
              </Button>
              {carta && <span className="max-w-56 truncate text-xs font-medium">{carta.name}</span>}
            </div>
            {/* El `accept` ya filtra formatos; el tope de tamaño solo se descubre al fallar, por eso queda visible. */}
            <Hint>{t('designations.form.cartaHint')}</Hint>
          </div>

          <FormDialogFooter
            error={error}
            pending={saving}
            submitLabel={t('designations.form.submit')}
            onCancel={() => handleOpenChange(false)}
          />
        </form>
        <InvitarExternoDialog
          businessId={businessId}
          open={altaOpen}
          onOpenChange={setAltaOpen}
          onInvited={(grant) => {
            setRecienDadaDeAlta(grant.employee)
            setEmployeeId(grant.employee.id)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
